import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useIsSizeRecommendationEnabled } from "@/hooks/use-is-size-recommendation-enabled";
import CopyBtn from "@/components/copy-btn";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Badge } from "@babackapp/ui/components/badge";
import { Navigate } from "react-router-dom";

const LIQUID_SNIPPET = `<button
  type="button"
  id="baback-size-button"
  class="baback-size-button"
  data-product-id="{{ product.id }}"
  data-variant-id="{{ product.selected_or_first_available_variant.id }}"
  style="display: none;"
>
  <span class="baback-default">Trouver ma taille</span>
  <span class="baback-custom" style="display: none;">
    Taille recommandée : <span class="baback-custom-res"></span>
  </span>
</button>`;

export default function SizingInstallPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const isSizingEnabled = useIsSizeRecommendationEnabled();

  if (!isSizingEnabled) {
    return <Navigate to={`/shops/${shop.id}`} replace />;
  }

  return (
    <div className="flex items-center justify-center">
      <div className="grid grid-cols-4 gap-4 w-full max-w-[1600px] lg:px-16 lg:py-6 px-6 py-4">
        <div className="col-span-4 mb-4 space-y-2">
          <h2 className="text-2xl leading-6 font-semibold text-foreground">
            {t("sizing.install.pageTitle")}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground max-w-[600px]">
            {t("sizing.install.pageSubtitle")}
          </p>
        </div>

        {/* Step 1 — App Embed */}
        <div className="col-span-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <StepBadge>1</StepBadge>
                <div>
                  <CardTitle>{t("sizing.install.step1.title")}</CardTitle>
                  <CardDescription className="mt-1">
                    {t("sizing.install.step1.description")}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                <li>{t("sizing.install.step1.instruction1")}</li>
                <li>{t("sizing.install.step1.instruction2")}</li>
                <li>{t("sizing.install.step1.instruction3")}</li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Step 2 — Button */}
        <div className="col-span-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <StepBadge>2</StepBadge>
                <div>
                  <CardTitle>{t("sizing.install.step2.title")}</CardTitle>
                  <CardDescription className="mt-1">
                    {t("sizing.install.step2.description")}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Option A — Theme editor block */}
              <div className="space-y-2">
                <p className="text-sm font-semibold">
                  {t("sizing.install.step2.optionA.label")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("sizing.install.step2.optionA.description")}
                </p>
                <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                  <li>{t("sizing.install.step2.optionA.instruction1")}</li>
                  <li>{t("sizing.install.step2.optionA.instruction2")}</li>
                  <li>{t("sizing.install.step2.optionA.instruction3")}</li>
                </ol>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">
                  {t("sizing.install.or")}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Option B — Liquid snippet */}
              <div className="space-y-2">
                <p className="text-sm font-semibold">
                  {t("sizing.install.step2.optionB.label")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("sizing.install.step2.optionB.description")}
                </p>
                <CopyBtn value={LIQUID_SNIPPET} className="w-full">
                  <pre className="w-full rounded-md bg-muted px-4 py-3 text-sm font-mono text-foreground overflow-x-auto whitespace-pre">
                    {LIQUID_SNIPPET}
                  </pre>
                </CopyBtn>
                <p className="text-xs text-muted-foreground">
                  {t("sizing.install.step2.optionB.hint")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function StepBadge({ children }: { children: React.ReactNode }) {
  return (
    <Badge
      variant="outline"
      className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
    >
      {children}
    </Badge>
  );
}
